<template>
  <div class="connection-manager" :class="{ 'portrait-mode': isPortraitMode }">
    <div
      class="main-interface"
      :class="{
        'video-minimized': isVideoMinimized,
        portrait: isPortraitMode,
        'windows-split': isWindowsSplit,
      }"
    >
      <div v-if="!isWindowsSplit" class="card-container" :class="{ portrait: isPortraitMode }">
        <VideoPanel
          v-show="!isVideoMinimized"
          :is-connected="isConnected"
          :is-user-speaking="isUserSpeaking"
          :is-portrait-mode="isPortraitMode"
          @minimize="minimizeVideo"
        />

        <AvatarPanel
          :full-width="isVideoMinimized || isPortraitMode"
          :is-agent-speaking="isAgentSpeaking"
          :is-agent-thinking="isAgentThinking"
          :avatar-image="avatarImage"
        />
      </div>

      <template v-else>
        <div class="split-mode-banner">
          <p>画面与形象已在两个独立浏览器窗口中显示</p>
          <span>左：摄像头画面 · 右：AI 形象与控制栏 · 本窗口仅保留会话</span>
          <button type="button" class="split-restore-btn" @click="restoreFromSplit">
            收回到主界面
          </button>
        </div>
        <div class="rtc-hidden-mount" aria-hidden="true">
          <VideoPanel
            mount-only
            :is-connected="isConnected"
            :is-user-speaking="isUserSpeaking"
            :is-portrait-mode="false"
          />
          <AvatarPanel
            :full-width="true"
            :is-agent-speaking="isAgentSpeaking"
            :is-agent-thinking="isAgentThinking"
            :avatar-image="avatarImage"
          />
        </div>
      </template>

      <div class="status-indicator" :class="connectStatus">
        {{
          connectStatus === 'connected'
            ? '在线'
            : connectStatus === 'reconnecting'
              ? `重连中(${reconnectAttempts}/${maxReconnectAttempts})`
              : '离线'
        }}
      </div>

      <!-- 隐藏文字聊天：Ctrl+Shift+Y 切换显示；发文字，智能体语音回复 -->
      <div v-show="showTextChatPanel" class="text-chat-panel">
        <div class="text-chat-header">
          <div class="text-chat-title">
            文字聊天
            <span class="text-chat-hint">Ctrl+Shift+Y 隐藏</span>
          </div>
          <button
            type="button"
            class="text-chat-close"
            title="隐藏 (Ctrl+Shift+Y)"
            @click="hideTextChatPanel"
          >
            ×
          </button>
        </div>
        <div class="text-chat-body">
          <div ref="textChatScrollContainer" class="text-chat-messages">
            <div v-if="chatMessages.length === 0" class="text-chat-empty">
              输入文字发送，智能体将语音回答
            </div>
            <div
              v-for="(item, index) in chatMessages"
              :key="`${item.role}-${index}-${item.timestamp.getTime()}`"
              class="text-chat-message"
              :class="item.role"
            >
              <div class="text-chat-role">
                {{ item.role === 'user' ? '我' : '智能体' }}
              </div>
              <div class="text-chat-content">{{ item.content }}</div>
            </div>
          </div>
          <div class="text-chat-input-area">
            <textarea
              ref="textChatInputRef"
              v-model="inputMessage"
              rows="2"
              placeholder="输入文字，Enter 发送，Shift+Enter 换行"
              @keydown.enter.exact.prevent="sendTextMessage"
            ></textarea>
            <button
              type="button"
              class="text-chat-send"
              :disabled="!inputMessage.trim() || !isConnected"
              @click="sendTextMessage"
            >
              发送
            </button>
          </div>
        </div>
      </div>

      <!-- 图片下载提示 -->
      <div
        v-for="(image, index) in downloadedImages"
        :key="image.originalUrl || image.url || image.timestamp"
        class="image-download-toast"
      >
        <img
          :src="image.url"
          :alt="'Downloaded image ' + (index + 1)"
          class="image-thumbnail"
          @click="openImagePreview(image.url)"
        />
        <div class="image-toast-content">
          <p>图片已生成</p>
          <p class="image-toast-hint">点击查看</p>
        </div>
        <div class="image-toast-actions">
          <a-button
            shape="circle"
            size="small"
            @click="saveImageToFile(image.url)"
            class="image-toast-download"
            title="保存图片"
          >
            <DownloadOutlined />
          </a-button>
          <a-button
            shape="circle"
            size="small"
            @click="removeImageToast(index)"
            class="image-toast-close"
          >
            <CloseOutlined />
          </a-button>
        </div>
      </div>

      <!-- 图片预览弹窗 -->
      <a-modal
        :open="imagePreviewVisible"
        :footer="null"
        :centered="true"
        :width="'auto'"
        :body-style="{ padding: '0', textAlign: 'center' }"
        @cancel="closeImagePreview"
        class="image-preview-modal"
      >
        <div class="preview-container">
          <img :src="previewImageUrl" alt="预览图片" class="preview-image" />
          <a-button
            class="preview-download-btn"
            @click="saveImageToFile(previewImageUrl)"
            type="primary"
          >
            <DownloadOutlined /> 保存图片
          </a-button>
        </div>
      </a-modal>

      <ControlsPanel
        v-if="!isWindowsSplit"
        :is-connected="isConnected"
        :is-connecting="isConnecting"
        :is-microphone-muted="isMicrophoneMuted"
        :is-volume-muted="isVolumeMuted"
        :volume="volume"
        :is-portrait-mode="isPortraitMode"
        @connect="handleConnectClick"
        @disconnect="handleDisconnect"
        @toggle-microphone="handleToggleMicrophone"
        @toggle-volume-mute="handleToggleVolumeMute"
        @volume-change="handleVolumeChange"
        @interrupt="handleInterrupt"
        @summarize="handleSummarize"
        @logout="handleLogout"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
} from 'vue';
import { message } from 'ant-design-vue';
import { EventNames, RealtimeClient, RealtimeUtils } from '@coze/realtime-api';
import { ChatEventType } from '@coze/api';
import { StreamIndex } from '@volcengine/rtc';
import CloseOutlined from '@ant-design/icons-vue/CloseOutlined';
import DownloadOutlined from '@ant-design/icons-vue/DownloadOutlined';

import VideoPanel from './VideoPanel.vue';
import AvatarPanel from './AvatarPanel.vue';
import ControlsPanel from './ControlsPanel.vue';
import { checkGestureTrigger } from './gestureConfig';
import { useLipSync } from '../composables/useLipSync';
import {
  closeSplitWindows,
  isSplitWindowsActive,
  openSplitWindows,
} from '../utils/splitWindows';
import { applyCozeSessionUpdate, type CozeSessionUpdateClient } from '../utils/cozeRealtimeSession';
import type { ConnectionBridge } from '../utils/connectionBridge';

// 扩展 Window 接口，添加 File System Access API
declare global {
  interface Window {
    showSaveFilePicker?: (options?: {
      suggestedName?: string;
      types?: Array<{
        description?: string;
        accept: Record<string, string[]>;
      }>;
    }) => Promise<FileSystemFileHandle>;
    __videoStream?: MediaStream;
    __avatarState?: {
      image: string;
      speaking: boolean;
      thinking: boolean;
    };
  }
}

interface FileSystemFileHandle {
  createWritable(): Promise<FileSystemWritableFileStream>;
}

interface FileSystemWritableFileStream extends WritableStream {
  write(data: Blob | string | ArrayBuffer): Promise<void>;
  close(): Promise<void>;
}

/**
 * 连接配置接口
 */
export interface ConnectionConfig {
  userId: string;
  botId: string;
  accessToken: string;
  voiceId: string;
  videoDeviceId: string;
  suppressStationaryNoise: boolean;
  suppressNonStationaryNoise: boolean;
  avatarImage: string;
  longestSilenceMs: number;
}

/**
 * 连接管理组件
 * 负责处理实时客户端连接、消息处理和视频管理
 */
export default defineComponent({
  name: 'ConnectionManager',
  emits: ['goBack'],
  components: {
    VideoPanel,
    AvatarPanel,
    ControlsPanel,
    CloseOutlined,
    DownloadOutlined,
  },
  props: {
    config: {
      type: Object as () => ConnectionConfig,
      required: true,
    },
  },
  setup(props, { emit }) {
    // 实时客户端实例
    const client = ref<RealtimeClient | null>(null);
    // 是否正在连接
    const isConnecting = ref(false);
    // 是否已连接
    const isConnected = ref(false);
    // 连接状态
    const connectStatus = ref<'connected' | 'disconnected' | 'reconnecting'>(
      'disconnected'
    );
    const reconnectAttempts = ref(0);
    const maxReconnectAttempts = ref(10);
    const isAutoReconnecting = ref(false);
    let reconnectTimer: number | null = null;
    let isManualDisconnecting = false;
    let suppressReconnectPrologue = false;
    let reconnectPrologueTimer: number | null = null;
    let agentThinkingTimer: number | null = null;
    const agentThinkingTimeoutMs = 12000;
    let muteKeepaliveTimer: number | null = null;
    const muteKeepaliveIntervalMs = 25000;
    // 视频设备列表
    const videoDevices = ref<MediaDeviceInfo[]>([]);
    // 选中的视频设备ID
    const selectedVideoDeviceId = ref('');
    // 是否为移动设备
    const isMobileDevice = ref(false);
    // 是否为竖屏模式
    const isPortraitMode = ref(false);
    const isWindowsSplit = ref(false);
    let splitWindowMonitorTimer: number | null = null;
    // 头像图片 URL
    const avatarImage = ref(props.config.avatarImage || '/xingxiaonge6c.gif');

    // 隐藏文字聊天（Ctrl+Shift+Y 切换）
    const showTextChatPanel = ref(false);
    const inputMessage = ref('');
    const chatMessages = ref<
      Array<{ role: 'user' | 'agent'; content: string; timestamp: Date }>
    >([]);
    const textChatScrollContainer = ref<HTMLElement | null>(null);
    const textChatInputRef = ref<HTMLTextAreaElement | null>(null);

    // 是否正在说话
    const isSpeaking = ref(false);
    // 智能体是否正在说话
    const isAgentSpeaking = ref(false);
    const isAgentThinking = ref(false);
    // 用户是否正在说话
    const isUserSpeaking = ref(false);
    // 视频是否最小化
    const isVideoMinimized = ref(false);

    // 麦克风状态
    const isMicrophoneMuted = ref(false);
    // 音量状态
    const isVolumeMuted = ref(false);
    // 音量大小
    const volume = ref(100);
    // 音频设备列表
    const audioDevices = ref<MediaDeviceInfo[]>([]);
    // 选中的音频设备ID
    const selectedAudioDeviceId = ref('');

    // 唇形同步相关
    const { startLipSync, stopLipSync } = useLipSync();

    // 消息处理相关状态
    let isSpeakingState = false;
    const triggeredInCurrentMessage = new Set<string>();
    let currentMessage = '';
    // 防抖函数，减少频繁的状态更新
    let speakingTimeout: number | null = null;
    let agentAvatarStopTimer: number | null = null;
    let agentAvatarStopAt = 0;

    const clearAgentAvatarStopTimer = (): void => {
      if (agentAvatarStopTimer !== null) {
        clearTimeout(agentAvatarStopTimer);
        agentAvatarStopTimer = null;
      }
    };

    const scheduleAgentAvatarStop = (delayMs = 12000): void => {
      clearAgentAvatarStopTimer();
      agentAvatarStopAt = Date.now() + delayMs;
      agentAvatarStopTimer = window.setTimeout(() => {
        if (Date.now() >= agentAvatarStopAt) {
          isAgentSpeaking.value = false;
          if (process.env.NODE_ENV === 'development') {
            console.log('[AvatarGif] stop by schedule', { delayMs });
          }
        }
      }, delayMs);
    };

    const keepAgentAvatarPlaying = (durationMs = 30000): void => {
      isAgentSpeaking.value = true;
      const nextStopAt = Date.now() + Math.min(durationMs, 180000);
      if (nextStopAt > agentAvatarStopAt) {
        scheduleAgentAvatarStop(nextStopAt - Date.now());
      }
      if (process.env.NODE_ENV === 'development') {
        console.log('[AvatarGif] keep playing', {
          durationMs,
          stopInMs: Math.max(0, agentAvatarStopAt - Date.now()),
        });
      }
    };

    const forceStopAgentAvatar = (): void => {
      clearAgentAvatarStopTimer();
      agentAvatarStopAt = 0;
      isAgentSpeaking.value = false;
    };

    const clearAgentThinkingTimer = (): void => {
      if (agentThinkingTimer !== null) {
        clearTimeout(agentThinkingTimer);
        agentThinkingTimer = null;
      }
    };

    const startAgentThinking = (): void => {
      if (!isConnected.value || isAgentSpeaking.value) {
        return;
      }

      isAgentThinking.value = true;
      clearAgentThinkingTimer();
      agentThinkingTimer = window.setTimeout(() => {
        agentThinkingTimer = null;
        if (isAgentThinking.value && !isAgentSpeaking.value) {
          isAgentThinking.value = false;
          if (process.env.NODE_ENV === 'development') {
            console.log(
              'Agent thinking cleared by timeout (silent/empty workflow reply)'
            );
          }
        }
      }, agentThinkingTimeoutMs);
    };

    const stopAgentThinking = (): void => {
      clearAgentThinkingTimer();
      isAgentThinking.value = false;
    };

    const estimateSpeechDurationMs = (text: string): number => {
      const normalizedLength = text.replace(/\s+/g, '').length;
      return Math.max(15000, Math.min(180000, normalizedLength * 420));
    };
    // 对话时间记录
    let conversationStartTime: number | null = null;
    let hasLoggedConversationId = false;

    // ★ 新增：用户语音转录文本（用于意图判断）
    let currentUserTranscript = '';
    // ★ 新增：当前对话轮次中用户是否请求了图片生成
    let userRequestedImage = false;
    // 当前轮次已处理的图片 URL（去重，避免 delta 片段重复触发）
    const processedImageUrls = new Set<string>();
    // 当前轮次是否已弹出过图片 toast（每轮只弹一次）
    let imageToastShown = false;
    // 图片链接消息由服务端直接合成语音，需要在客户端截断/静音兜底
    let suppressingImageLinkSpeech = false;
    let imageLinkSpeechRestoreTimer: number | null = null;
    let preMutedImageResponseSpeech = false;

    // 下载的图片
    const downloadedImages = ref<
      Array<{ url: string; originalUrl?: string; timestamp: number }>
    >([]);

    // 图片预览弹窗状态
    const imagePreviewVisible = ref(false);
    const previewImageUrl = ref('');

    // Web Audio API 相关变量（用于全局音量控制）
    let audioContext: AudioContext | null = null;
    let gainNode: GainNode | null = null;
    let audioInitialized = false;
    const connectedElements = new Set<HTMLMediaElement>();
    let mediaObserver: MutationObserver | null = null;

    // 屏幕录制相关变量
    let screenStream: MediaStream | null = null;
    let microphoneStream: MediaStream | null = null;
    let combinedStream: MediaStream | null = null;
    let mediaRecorder: MediaRecorder | null = null;
    let recordedChunks: Blob[] = [];
    let recordingAudioContext: AudioContext | null = null;
    let audioDestination: MediaStreamAudioDestinationNode | null = null;

    /**
     * 初始化Web Audio API用于全局音量控制
     */
    const initWebAudioAPI = (): void => {
      try {
        if (audioInitialized) return;

        // 创建AudioContext
        audioContext = new (
          window.AudioContext || (window as any).webkitAudioContext
        )();

        // 创建GainNode用于音量控制
        gainNode = audioContext.createGain();

        // 连接到输出
        gainNode.connect(audioContext.destination);

        // 设置初始音量
        gainNode.gain.value = isVolumeMuted.value ? 0 : volume.value / 100;

        initMediaObserver();

        audioInitialized = true;

        if (process.env.NODE_ENV === 'development') {
          console.log('Web Audio API initialized for volume control');
        }
      } catch (error: any) {
        if (process.env.NODE_ENV === 'development') {
          console.error(
            'Failed to initialize Web Audio API:',
            error.message || error
          );
        }
      }
    };

    const connectMediaElementToGain = (element: HTMLMediaElement): void => {
      if (!audioContext || !gainNode || connectedElements.has(element)) {
        return;
      }

      try {
        if (audioContext.state === 'suspended') {
          audioContext
            .resume()
            .then(() => connectMediaElementToGain(element))
            .catch((error) => {
              if (process.env.NODE_ENV === 'development') {
                console.warn('Failed to resume AudioContext:', error);
              }
            });
          return;
        }

        const source = audioContext.createMediaElementSource(element);
        source.connect(gainNode);
        connectedElements.add(element);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to connect media element to GainNode:', error);
        }
      } finally {
        element.volume = isVolumeMuted.value ? 0 : volume.value / 100;
      }
    };

    const scanAndConnectMediaElements = (): void => {
      document.querySelectorAll('audio, video').forEach((element) => {
        if (element instanceof HTMLMediaElement) {
          connectMediaElementToGain(element);
          element.volume = isVolumeMuted.value ? 0 : volume.value / 100;
        }
      });

      document
        .querySelectorAll(
          '[data-coze-audio], [class*="coze"][class*="audio"], [id*="audio"], [id*="player"], [class*="audio"]'
        )
        .forEach((element) => {
          if (element instanceof HTMLMediaElement) {
            connectMediaElementToGain(element);
            return;
          }

          if (element instanceof HTMLElement) {
            const innerAudio = element.querySelector('audio');
            if (innerAudio instanceof HTMLMediaElement) {
              connectMediaElementToGain(innerAudio);
            }
          }
        });
    };

    const initMediaObserver = (): void => {
      if (mediaObserver) return;

      mediaObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLMediaElement) {
              connectMediaElementToGain(node);
              setPlaybackVolume(isVolumeMuted.value ? 0 : volume.value);
              return;
            }

            if (node instanceof HTMLElement) {
              node.querySelectorAll('audio, video').forEach((element) => {
                if (element instanceof HTMLMediaElement) {
                  connectMediaElementToGain(element);
                }
              });
              setPlaybackVolume(isVolumeMuted.value ? 0 : volume.value);
            }
          });
        });
      });

      mediaObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      scanAndConnectMediaElements();
    };

    const cleanupMediaObserver = (): void => {
      if (mediaObserver) {
        mediaObserver.disconnect();
        mediaObserver = null;
      }
      connectedElements.clear();
    };

    /**
     * 检查是否为移动设备
     * @returns boolean
     */
    const isMobile = (): boolean => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    };

    /**
     * 检查是否为竖屏模式
     * @returns boolean
     */
    const checkPortraitMode = (): boolean => {
      return window.innerHeight > window.innerWidth;
    };

    /**
     * 更新屏幕方向状态
     */
    const updatePortraitMode = (): void => {
      isPortraitMode.value = checkPortraitMode();
      if (process.env.NODE_ENV === 'development') {
        console.log('Portrait mode updated:', isPortraitMode.value);
      }
    };

    const syncLayoutMode = (): void => {
      isMobileDevice.value = isMobile();
      updatePortraitMode();
    };

    const syncAvatarStateToWindow = (): void => {
      window.__avatarState = {
        image: avatarImage.value,
        speaking: isAgentSpeaking.value,
        thinking: isAgentThinking.value,
      };
    };

    const cacheVideoStreamForPopup = (): void => {
      const videoElement = document.querySelector(
        '#local-player video'
      ) as HTMLVideoElement | null;
      if (videoElement?.srcObject) {
        window.__videoStream = videoElement.srcObject as MediaStream;
      }
    };

    const clearSplitWindowMonitor = (): void => {
      if (splitWindowMonitorTimer !== null) {
        window.clearInterval(splitWindowMonitorTimer);
        splitWindowMonitorTimer = null;
      }
    };

    const startSplitWindowMonitor = (): void => {
      clearSplitWindowMonitor();
      splitWindowMonitorTimer = window.setInterval(() => {
        if (isWindowsSplit.value && !isSplitWindowsActive()) {
          restoreFromSplit(false);
        }
      }, 800);
    };

    const restoreFromSplit = (closePopups = true): void => {
      clearSplitWindowMonitor();
      if (closePopups) {
        closeSplitWindows();
      }
      isWindowsSplit.value = false;
      isVideoMinimized.value = false;
    };

    const openSplitWindowsFromMain = (): void => {
      cacheVideoStreamForPopup();
      syncAvatarStateToWindow();

      const { video, avatar } = openSplitWindows();
      if (!video || !avatar) {
        closeSplitWindows();
        message.error(
          '需要同时打开两个窗口。请在浏览器地址栏允许弹窗后，再点击分离按钮或按 Ctrl+Shift+S'
        );
        return;
      }

      isWindowsSplit.value = true;
      isVideoMinimized.value = false;
      startSplitWindowMonitor();
      message.success('已分离：左侧为画面窗口，右侧为形象窗口');
    };

    const handleToggleSplitWindows = (): void => {
      if (isWindowsSplit.value) {
        restoreFromSplit();
        message.info('已收回到主界面');
        return;
      }
      openSplitWindowsFromMain();
    };

    const scrollTextChatToBottom = (): void => {
      nextTick(() => {
        const el = textChatScrollContainer.value;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    };

    const appendTextChatMessage = (
      role: 'user' | 'agent',
      content: string
    ): void => {
      const trimmed = content.trim();
      if (!trimmed) {
        return;
      }
      const last = chatMessages.value[chatMessages.value.length - 1];
      if (last && last.role === role && last.content === trimmed) {
        return;
      }
      chatMessages.value.push({
        role,
        content: trimmed,
        timestamp: new Date(),
      });
      scrollTextChatToBottom();
    };

    const focusTextChatInput = (): void => {
      nextTick(() => {
        textChatInputRef.value?.focus();
      });
    };

    const showTextChat = (): void => {
      showTextChatPanel.value = true;
      focusTextChatInput();
    };

    const hideTextChatPanel = (): void => {
      showTextChatPanel.value = false;
    };

    const toggleTextChatPanel = (): void => {
      showTextChatPanel.value = !showTextChatPanel.value;
      if (showTextChatPanel.value) {
        focusTextChatInput();
        message.info('文字聊天已显示（Ctrl+Shift+Y 隐藏）');
      } else {
        message.info('文字聊天已隐藏');
      }
    };

    const sendTextMessage = async (): Promise<void> => {
      const content = inputMessage.value.trim();
      if (!content) {
        return;
      }
      if (!client.value || !isConnected.value) {
        message.warning('请先连接后再发送文字');
        return;
      }

      appendTextChatMessage('user', content);
      inputMessage.value = '';

      try {
        await client.value.sendMessage({
          id: `event_${Date.now()}`,
          event_type: 'conversation.message.create',
          data: {
            role: 'user',
            content_type: 'text',
            content,
          },
        });
        startAgentThinking();
      } catch (error: any) {
        message.error(error?.message || '发送消息失败');
      }
    };

    const handleSplitWindowsShortcut = (event: KeyboardEvent): void => {
      // 文字聊天面板：Ctrl+Shift+Y（输入框内也可切换）
      if (
        event.ctrlKey &&
        event.shiftKey &&
        event.key.toLowerCase() === 'y'
      ) {
        event.preventDefault();
        toggleTextChatPanel();
        return;
      }

      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }

      if (!event.ctrlKey || !event.shiftKey || event.key.toLowerCase() !== 's') {
        return;
      }

      event.preventDefault();
      handleToggleSplitWindows();
    };

    const publishConnectionBridge = (): void => {
      const bridge: ConnectionBridge = {
        getState: () => ({
          isConnected: isConnected.value,
          isConnecting: isConnecting.value,
          isMicrophoneMuted: isMicrophoneMuted.value,
          isVolumeMuted: isVolumeMuted.value,
          volume: volume.value,
          isPortraitMode: isPortraitMode.value,
          isWindowsSplit: isWindowsSplit.value,
          isAgentSpeaking: isAgentSpeaking.value,
          isAgentThinking: isAgentThinking.value,
          avatarImage: avatarImage.value,
          connectStatus: connectStatus.value,
        }),
        connect: () => {
          handleConnectClick();
        },
        disconnect: () => {
          handleDisconnect();
        },
        toggleMicrophone: () => {
          void handleToggleMicrophone();
        },
        toggleVolumeMute: () => {
          handleToggleVolumeMute();
        },
        volumeChange: (value: number) => {
          handleVolumeChange(value);
        },
        interrupt: () => {
          handleInterrupt();
        },
        summarize: () => {
          void handleSummarize();
        },
        toggleSplitWindows: () => {
          handleToggleSplitWindows();
        },
        logout: () => {
          handleLogout();
        },
      };

      window.__connectionBridge = bridge;
    };

    const clearConnectionBridge = (): void => {
      if (window.__connectionBridge) {
        delete window.__connectionBridge;
      }
    };

    const getPermissionErrorMessage = (error: any): string => {
      const errorName = error?.name || '';
      if (
        errorName === 'NotAllowedError' ||
        errorName === 'PermissionDeniedError'
      ) {
        return '需要设备访问权限，请在浏览器中允许麦克风或摄像头访问';
      }
      if (
        errorName === 'NotFoundError' ||
        errorName === 'DevicesNotFoundError'
      ) {
        return '未检测到可用的麦克风或摄像头设备';
      }
      if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
        return '设备正在被其他应用占用，请关闭后重试';
      }
      if (errorName === 'SecurityError' || errorName === 'TypeError') {
        return '当前页面环境不支持设备权限检查，请确认使用 HTTPS 或本地开发环境';
      }

      return error?.message || '设备权限检查失败';
    };

    const stopStreamTracks = (stream: MediaStream | null): void => {
      stream?.getTracks().forEach((track) => track.stop());
    };

    const extractConversationId = (event: any): string | null => {
      const candidates = [
        event?.data?.conversation_id,
        event?.data?.conversationId,
        event?.data?.id,
        event?.data?.conversation?.id,
        event?.detail?.conversation_id,
        event?.detail?.conversationId,
        event?.conversation_id,
        event?.conversationId,
      ];

      const conversationId = candidates.find(
        (value) => value !== undefined && value !== null && `${value}`.trim()
      );

      return conversationId ? String(conversationId) : null;
    };

    const getStoredConversationId = (): string | undefined => {
      const conversationId = sessionStorage
        .getItem('coze_conversation_id')
        ?.trim();
      return conversationId || undefined;
    };

    const logConversationIdOnce = (event: any): void => {
      if (hasLoggedConversationId) {
        return;
      }

      const conversationId = extractConversationId(event);
      if (!conversationId) {
        return;
      }

      console.log(`Conversation ID: ${conversationId}`);
      sessionStorage.setItem('coze_conversation_id', conversationId);
      hasLoggedConversationId = true;
      void fetch('/api/coze/conversation/current', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: conversationId,
        }),
        credentials: 'include',
      }).catch((error) => {
        console.error('Failed to persist Coze conversation ID:', error);
      });
    };

    const checkDevicePermissionCompat = async (
      requestVideo: boolean
    ): Promise<{ audio: boolean; video: boolean }> => {
      try {
        const permission =
          await RealtimeUtils.checkDevicePermission(requestVideo);
        if (requestVideo && !permission.video) {
          let videoStream: MediaStream | null = null;
          try {
            videoStream = await navigator.mediaDevices.getUserMedia({
              audio: false,
              video: true,
            });
            permission.video = videoStream.getVideoTracks().length > 0;
          } catch (videoError: any) {
            if (process.env.NODE_ENV === 'development') {
              console.warn(
                'Browser video permission check failed after SDK check:',
                videoError
              );
            }
          } finally {
            stopStreamTracks(videoStream);
          }
        }
        return permission;
      } catch (error: any) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            'RealtimeUtils.checkDevicePermission failed, falling back to getUserMedia:',
            error
          );
        }

        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('当前浏览器不支持媒体设备访问');
        }

        let audioStream: MediaStream | null = null;
        let videoStream: MediaStream | null = null;

        try {
          audioStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false,
          });

          let hasVideoPermission = false;
          if (requestVideo) {
            try {
              videoStream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: true,
              });
              hasVideoPermission = videoStream.getVideoTracks().length > 0;
            } catch (videoError: any) {
              if (process.env.NODE_ENV === 'development') {
                console.warn(
                  'Video permission fallback check failed, continuing with audio only:',
                  videoError
                );
              }
            }
          }

          return {
            audio: audioStream.getAudioTracks().length > 0,
            video: requestVideo ? hasVideoPermission : false,
          };
        } catch (fallbackError: any) {
          throw new Error(getPermissionErrorMessage(fallbackError));
        } finally {
          stopStreamTracks(audioStream);
          stopStreamTracks(videoStream);
        }
      }
    };

    /**
     * 建连后更新 Realtime 会话：延长静默超时，避免拍课长等待断连（4029）
     * 与 video 项目一致：不阻塞建连/对话，后台发送一次即可
     */
    const applyRealtimeSessionSettings = (
      targetClient: CozeSessionUpdateClient
    ): void => {
      void applyCozeSessionUpdate(targetClient, {
        longestSilenceMs: props.config.longestSilenceMs,
      })
        .then(() => {
          if (process.env.NODE_ENV === 'development') {
            console.log('Applied Coze session.update', {
              longestSilenceMs: props.config.longestSilenceMs,
            });
          }
        })
        .catch((error) => {
          console.warn('Failed to apply Coze session.update:', error);
        });
    };

    /**
     * 初始化客户端
     * @returns Promise<void>
     */
    const initClient = async (
      options: { silentReconnect?: boolean } = {}
    ): Promise<void> => {
      if (client.value) {
        return;
      }
      syncLayoutMode();

      const permission = await checkDevicePermissionCompat(true);
      if (!permission.audio) {
        throw new Error('需要麦克风访问权限');
      }

      // 检查RTCPeerConnection是否可用
      if (!window.RTCPeerConnection) {
        throw new Error('您的设备不支持WebRTC，无法进行语音通话');
      }

      if (isMobileDevice.value && permission.video) {
        videoDevices.value = [
          { deviceId: 'user', label: '前置摄像头' } as MediaDeviceInfo,
          { deviceId: 'environment', label: '后置摄像头' } as MediaDeviceInfo,
        ];
      } else {
        const devices = await RealtimeUtils.getAudioDevices({
          video: true,
        });
        videoDevices.value = devices.videoInputs || [];
        audioDevices.value = devices.audioInputs || [];
        if (audioDevices.value.length > 0) {
          selectedAudioDeviceId.value = audioDevices.value[0].deviceId;
        }
      }

      // 不使用后端配置的视频设备ID，始终使用默认摄像头
      if (!selectedVideoDeviceId.value && videoDevices.value.length > 0) {
        selectedVideoDeviceId.value = videoDevices.value[0].deviceId;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Initializing client with botId:', props.config.botId);
        console.log('Noise suppression config:', {
          suppressStationaryNoise: props.config.suppressStationaryNoise,
          suppressNonStationaryNoise: props.config.suppressNonStationaryNoise,
        });
      }

      client.value = new RealtimeClient({
        accessToken: () => props.config.accessToken,
        botId: props.config.botId,
        conversationId: options.silentReconnect
          ? getStoredConversationId()
          : undefined,
        prologueContent: options.silentReconnect ? ' ' : undefined,
        userId: props.config.userId,
        connectorId: '1024',
        voiceId:
          props.config.voiceId && props.config.voiceId.trim()
            ? props.config.voiceId
            : undefined,
        suppressStationaryNoise: props.config.suppressStationaryNoise,
        suppressNonStationaryNoise: props.config.suppressNonStationaryNoise,
        allowPersonalAccessTokenInBrowser: true,
        debug: process.env.NODE_ENV === 'development',
        videoConfig: permission.video
          ? ({
              renderDom: 'local-player',
              videoOnDefault: true,
              videoInputDeviceId: selectedVideoDeviceId.value || undefined,
              mirror: false /* 确保视频不镜像显示 */,
            } as any)
          : undefined,
      });

      client.value.on('server.audio.user.speech_started', () => {
        restorePlaybackAfterImageHandling();
        currentUserTranscript = '';
        userRequestedImage = false;
        processedImageUrls.clear();
        imageToastShown = false;
        stopAgentThinking();
        isSpeaking.value = true;
        isUserSpeaking.value = true;
      });
      client.value.on('server.audio.user.speech_stopped', () => {
        isSpeaking.value = false;
        isUserSpeaking.value = false;
        startAgentThinking();
      });

      client.value.on(EventNames.SESSION_UPDATED, (event: any) => {
        const ms = event?.data?.longest_silence_ms;
        if (typeof ms === 'number' && ms > 0 && process.env.NODE_ENV === 'development') {
          console.log('[Coze] server.session.updated', { longestSilenceMs: ms });
        }
      });

      // 工作流刻意空输出时 Coze 可能下发 chat.failed，属正常静默轮次，不向用户报错
      client.value.on(EventNames.CONVERSATION_CHAT_FAILED, (event: any) => {
        stopAgentThinking();
        if (process.env.NODE_ENV === 'development') {
          console.log('[Coze] 对话轮次结束（工作流静默输出）', event);
        }
      });
      client.value.on(EventNames.CONVERSATION_CREATED, (event: any) => {
        logConversationIdOnce(event);
      });

      // ★ 修改：不再单独注册 CONVERSATION_AUDIO_TRANSCRIPT_DELTA listener
      // 改为在 ALL_SERVER handler 内部统一处理，避免事件被消费导致 listener 不触发

      client.value.on('server.audio.agent.speech_started', () => {
        stopAgentThinking();
        if (suppressReconnectPrologue) {
          setPlaybackVolume(0);
          void client.value?.interrupt().catch((error) => {
            if (process.env.NODE_ENV === 'development') {
              console.warn('Failed to interrupt reconnect prologue:', error);
            }
          });
          return;
        }

        if (userRequestedImage) {
          preMutedImageResponseSpeech = true;
          setPlaybackVolume(0);
          if (process.env.NODE_ENV === 'development') {
            console.log(
              'Pre-muted assistant speech while waiting for image result'
            );
          }
        }
        isSpeaking.value = true;
        keepAgentAvatarPlaying(60000);
        startLipSync();
        // 记录对话开始时间
        if (!conversationStartTime) {
          conversationStartTime = Date.now();
        }
      });
      client.value.on('server.audio.agent.speech_stopped', () => {
        if (suppressReconnectPrologue) {
          suppressReconnectPrologue = false;
          clearReconnectPrologueTimer();
          setPlaybackVolume(isVolumeMuted.value ? 0 : volume.value);
          return;
        }

        isSpeaking.value = false;
        scheduleAgentAvatarStop(12000);
        stopLipSync();
        if (
          preMutedImageResponseSpeech &&
          !isVolumeMuted.value &&
          !suppressingImageLinkSpeech
        ) {
          setPlaybackVolume(volume.value);
        }
        preMutedImageResponseSpeech = false;
        // 计算对话消耗的时间
        if (conversationStartTime) {
          const duration = Math.round(
            (Date.now() - conversationStartTime) / 1000
          );
          if (duration > 0) {
            // 调用API更新剩余时间
            updateRemainingTime(duration);
          }
          conversationStartTime = null;
        }
      });

      client.value.on(EventNames.DISCONNECTED, (event: any) => {
        handleRealtimeDisconnect('disconnected', event);
      });

      client.value.on(EventNames.ERROR, (event: any) => {
        handleRealtimeDisconnect('error', event);
      });

      client.value.on(EventNames.BOT_LEAVE, (event: any) => {
        handleRealtimeDisconnect('bot leave', event);
      });

      handleMessageEvent();
    };

    /**
     * 处理消息事件
     */
    const handleMessageEvent = (): void => {
      const eventClient = client.value;
      eventClient?.on(
        EventNames.ALL_SERVER,
        (eventName: string, event: any) => {
          if (client.value !== eventClient) {
            return;
          }

          logConversationIdOnce(event);
          let content = event?.data?.content || '';
          content = filterVideoFrameLinks(content);

          // 跳过 JSON 元数据内容（bot 插件调用结果、上下文、工作流等，不是用户可读文本）
          if (content) {
            const trimmed = content.trimStart();
            const isJsonPayload =
              trimmed.startsWith('{') || trimmed.startsWith('["{');
            const isMetadata =
              content.includes('bot_context') ||
              content.includes('_WORKFLOW_INPUT') ||
              content.includes('chat_history') ||
              content.includes('connector_uid') ||
              content.includes('conversation_type') ||
              content.includes('plugin_icon') ||
              content.includes('pe_result') ||
              content.includes('log_id') ||
              content.includes('\"code\":') ||
              content.includes('"code":');
            if (isJsonPayload || isMetadata) {
              return;
            }
          }

          // ★ 新增：区分用户消息和 bot 消息
          const messageRole = event?.data?.role || event?.detail?.role || '';
          const isUserMessage = messageRole === 'user';

          const isDelta =
            event?.event_type === ChatEventType.CONVERSATION_MESSAGE_DELTA ||
            (typeof eventName === 'string' &&
              eventName.toLowerCase().includes('message') &&
              eventName.toLowerCase().includes('delta'));
          const isCompleted =
            event?.event_type ===
              ChatEventType.CONVERSATION_MESSAGE_COMPLETED ||
            (typeof eventName === 'string' &&
              eventName.toLowerCase().includes('message') &&
              eventName.toLowerCase().includes('completed'));

          // ★ 新增：在 return 之前捕获用户语音转录事件，进行图片意图判断
          if (
            eventName === 'server.conversation.audio_transcript.delta' ||
            event?.event_type === 'conversation.audio_transcript.delta'
          ) {
            const transcript = event?.data?.content || '';
            if (transcript) {
              if (!currentUserTranscript) {
                restorePlaybackAfterImageHandling();
                userRequestedImage = false;
                processedImageUrls.clear();
                imageToastShown = false;
              }
              currentUserTranscript += transcript;
              if (detectImageIntent(currentUserTranscript)) {
                userRequestedImage = true;
              }
              if (process.env.NODE_ENV === 'development') {
                console.log(
                  'User transcript:',
                  transcript,
                  '| Cumulative:',
                  currentUserTranscript,
                  '| Image intent:',
                  userRequestedImage
                );
              }
            }
            return; // transcript 事件不需要继续处理消息逻辑
          }

          if (!content && !isDelta && !isCompleted) return;

          // ★ 修改：用户消息 echo 不处理图片逻辑
          if (isUserMessage) {
            if (isCompleted || isDelta) {
              startAgentThinking();
            }
            return;
          }

          // 只在开发模式下输出详细日志
          if (process.env.NODE_ENV === 'development') {
            console.log('Message event received:', {
              eventName,
              eventType: event?.event_type,
              role: messageRole,
              content:
                content.length > 50
                  ? content.substring(0, 50) + '...'
                  : content,
              isDelta,
              isCompleted,
            });
          }

          if (content) {
            stopAgentThinking();
            // ★ 修复：当 bot 返回 function_call 类型消息（如图片生成插件）时，
            // 也尝试从中提取图片链接并下载
            const isFunctionCall =
              event?.data?.type === 'function_call' ||
              (content &&
                (content.includes('generateImage') ||
                  content.includes('generate_image') ||
                  content.includes('图片生成')));

            if (isFunctionCall) {
              // 尝试从纯文本中提取 URL
              let urlsInFunctionCall: string[] = [];
              try {
                // 如果内容是 JSON，尝试解析其中的 URL
                if (
                  content.trim().startsWith('{') ||
                  content.trim().startsWith('[')
                ) {
                  const parsed = JSON.parse(content);
                  const jsonStr = JSON.stringify(parsed);
                  urlsInFunctionCall =
                    jsonStr.match(/https?:\/\/[^\s"')，,。\\\]]+/g) || [];
                } else {
                  urlsInFunctionCall =
                    content.match(/https?:\/\/[^\s"')，,。]+/g) || [];
                }
              } catch {
                urlsInFunctionCall =
                  content.match(/https?:\/\/[^\s"')，,。]+/g) || [];
              }

              const validImageUrls = urlsInFunctionCall.filter(
                (url) =>
                  /\.(jpg|jpeg|png|gif|webp|bmp|svg)\b/i.test(url) ||
                  /artifact|image\/|img\/|obj\/|bot-studio-plugin|plugin-resource/.test(
                    url
                  )
              );
              if (validImageUrls.length === 0) {
                return;
              }
              validImageUrls.forEach((link) => {
                if (!processedImageUrls.has(link)) {
                  processedImageUrls.add(link);
                  downloadImage(link, !imageToastShown);
                  imageToastShown = true;
                }
              });
              userRequestedImage = true;

              // 函数调用结果不需要朗读，静默跳过
              if (process.env.NODE_ENV === 'development') {
                console.log(
                  'Function call image result detected, downloading image silently:',
                  validImageUrls
                );
              }
              return;
            }

            // 检测并下载图片 — 仅在用户明确请求图片生成时才触发
            const imageLinks = detectImageLinks(content);
            const isImageGenerationReply = isLikelyImageGenerationReply(
              content,
              imageLinks
            );
            if (
              imageLinks.length > 0 &&
              (userRequestedImage || isImageGenerationReply)
            ) {
              imageLinks.forEach((link) => {
                if (!processedImageUrls.has(link)) {
                  processedImageUrls.add(link);
                  downloadImage(link, !imageToastShown);
                  imageToastShown = true;
                }
              });
              userRequestedImage = true;
            } else if (imageLinks.length > 0 && !userRequestedImage) {
              // 用户没有请求图片但 bot 返回了图片链接，静默忽略
              if (process.env.NODE_ENV === 'development') {
                console.log(
                  'Bot returned image links but user did not request image generation, ignoring:',
                  imageLinks
                );
              }
            }

            // 过滤语音合成中的图片链接
            if (
              shouldSuppressImageLinkMessage(content, imageLinks, isCompleted)
            ) {
              if (process.env.NODE_ENV === 'development') {
                console.log(
                  'Suppressing image-link speech for assistant message:',
                  content
                );
              }
              suppressImageLinkSpeechPlayback();
              preMutedImageResponseSpeech = false;
              currentMessage = '';
              if (speakingTimeout) {
                clearTimeout(speakingTimeout);
                speakingTimeout = null;
              }
              return;
            }

            const filteredContent = filterImageLinksForSpeech(content);

            if (
              preMutedImageResponseSpeech &&
              filteredContent &&
              !imageLinks.length
            ) {
              restorePlaybackAfterImageHandling();
              userRequestedImage = false;
              processedImageUrls.clear();
              imageToastShown = false;
            }

            // 不再添加 [图片已为您生成] 等文本，避免语音朗读多余内容
            currentMessage += filteredContent;

            // 对累积的消息进行最终检查，确保移除任何包含URL或图片链接描述的内容行
            const lines = currentMessage.split('\n');
            const filteredLines = lines.filter((line) => {
              // 移除包含URL的行
              if (/https?:\/\//.test(line)) return false;
              // 移除只包含图片链接描述的行（如"图片链接："、"点击链接查看"等）
              if (
                /^(图片链接|生成图链接|链接如下|链接地址|链接为|链接是|链接|点击链接查看|点击查看|请查看|查看详情)[：:]*\s*$/g.test(
                  line
                )
              )
                return false;
              return true;
            });
            currentMessage = filteredLines.join('\n');
            keepAgentAvatarPlaying(estimateSpeechDurationMs(currentMessage));

            if (!isSpeakingState) {
              isSpeaking.value = true;
              // 只有智能体的消息才设置isAgentSpeaking为true
              // 这里通过eventType来判断是否是智能体的消息
              if (
                event?.event_type ===
                  ChatEventType.CONVERSATION_MESSAGE_DELTA ||
                event?.event_type ===
                  ChatEventType.CONVERSATION_MESSAGE_COMPLETED
              ) {
                keepAgentAvatarPlaying(
                  estimateSpeechDurationMs(currentMessage)
                );
              }
              isSpeakingState = true;
              startLipSync();
            } else {
              // 如果正在处理消息，重置防抖超时
              if (speakingTimeout) {
                clearTimeout(speakingTimeout);
              }
            }
          }

          if (isCompleted) {
            // 静默轮次（工作流 output 为空）不会触发 agent.speech_started，在此结束思考态
            if (!isUserMessage) {
              stopAgentThinking();
              if (currentMessage.trim()) {
                appendTextChatMessage('agent', currentMessage);
              }
            }

            // 清除之前的超时
            if (speakingTimeout) {
              clearTimeout(speakingTimeout);
            }

            // 防抖处理，避免频繁更新
            speakingTimeout = window.setTimeout(() => {
              if (isSpeakingState) {
                isSpeaking.value = false;
                scheduleAgentAvatarStop(
                  estimateSpeechDurationMs(currentMessage)
                );
                isSpeakingState = false;
                stopLipSync();
              }

              // 处理手势触发
              if (currentMessage) {
                const trigger = checkGestureTrigger(
                  currentMessage,
                  triggeredInCurrentMessage
                );
                if (trigger && process.env.NODE_ENV === 'development') {
                  triggeredInCurrentMessage.add(trigger.name);
                  console.log(`Gesture triggered: ${trigger.name}`);
                }
              }

              currentMessage = '';
              triggeredInCurrentMessage.clear();
            }, 2000);
          }

          // ★ 修改：在整个对话轮次完成时重置用户转录和图片意图
          // 不在单个 message.completed 时重置，因为 bot 的图片链接可能在后续消息中才到达
          if (
            eventName === 'server.conversation.chat.completed' ||
            event?.event_type === 'conversation.chat.completed'
          ) {
            currentUserTranscript = '';
            userRequestedImage = false;
            processedImageUrls.clear();
            imageToastShown = false;
            stopAgentThinking();
            restorePlaybackAfterImageHandling();
          }

          if (eventName === 'server.audio.user.speech_stopped') {
            isSpeaking.value = false;
          }
        }
      );
    };

    /**
     * 过滤视频帧链接
     * @param content 消息内容
     * @returns 过滤后的内容
     */
    const filterVideoFrameLinks = (content: string): string => {
      if (!content) return content;
      // 匹配 markdown 格式的视频帧链接 ! `url`
      let filteredContent = content.replace(
        /^\s*(! `https:\/\/lf[0-9]+-bot-platform-tos-sign\.coze\.cn\/bot-studio-bot-platform\/coze_rtc\/video_frame_.*?`\s*)+/,
        ''
      );
      // 过滤所有 video_frame URL（包括 JSON 中嵌套的）
      filteredContent = filteredContent.replace(
        /https?:\/\/[^\s]*video_frame_[^\s]*/gi,
        ''
      );
      filteredContent = filteredContent.trim();
      return filteredContent;
    };

    /**
     * 保存录制的视频文件
     * @param blob 视频数据 Blob
     */
    const saveRecordedVideo = async (blob: Blob): Promise<void> => {
      try {
        const timestamp = new Date()
          .toISOString()
          .replace(/[:.]/g, '-')
          .slice(0, 19);
        const fileName = `call_recording_${timestamp}.webm`;

        // 优先使用 File System Access API
        if (window.showSaveFilePicker) {
          try {
            const handle = await window.showSaveFilePicker({
              suggestedName: fileName,
              types: [
                {
                  description: 'WebM Video',
                  accept: { 'video/webm': ['.webm'] },
                },
              ],
            });

            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();

            message.success('录制视频已保存');
            return;
          } catch (error: any) {
            // 用户取消保存
            if (error.name === 'AbortError') {
              return;
            }
            console.warn('showSaveFilePicker 失败，使用降级方案:', error);
          }
        }

        // 降级方案：使用 <a> 元素下载
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // 延迟释放 URL
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        message.success('录制视频已下载');
      } catch (error: any) {
        console.error('保存录制视频失败:', error);
        message.error('保存录制视频失败');
      }
    };

    /**
     * 启动屏幕录制
     */
    const startScreenRecording = async (): Promise<void> => {
      try {
        // 清理旧的录制资源
        if (screenStream) {
          screenStream.getTracks().forEach((track) => track.stop());
          screenStream = null;
        }
        if (microphoneStream) {
          microphoneStream.getTracks().forEach((track) => track.stop());
          microphoneStream = null;
        }
        if (combinedStream) {
          combinedStream.getTracks().forEach((track) => track.stop());
          combinedStream = null;
        }
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
          mediaRecorder = null;
        }
        if (recordingAudioContext) {
          recordingAudioContext.close();
          recordingAudioContext = null;
        }
        recordedChunks = [];

        // 1. 获取屏幕流（包含系统音频）
        screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            displaySurface: 'browser',
            cursor: 'always',
          } as any,
          audio: true, // 启用系统音频录制
        });

        // 2. 获取麦克风音频流
        try {
          microphoneStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          });
        } catch (micError: any) {
          console.warn('无法获取麦克风音频:', micError.message);
          message.warning('无法访问麦克风，录制将只包含系统音频');
        }

        // 3. 合并音频轨道（混合系统音频和麦克风音频）
        combinedStream = new MediaStream();

        // 添加视频轨道
        const videoTracks = screenStream.getVideoTracks();
        if (videoTracks.length > 0) {
          combinedStream.addTrack(videoTracks[0]);
        }

        // 混合音频轨道
        const screenAudioTracks = screenStream.getAudioTracks();
        const micAudioTracks = microphoneStream
          ? microphoneStream.getAudioTracks()
          : [];

        if (screenAudioTracks.length > 0 || micAudioTracks.length > 0) {
          try {
            recordingAudioContext = new AudioContext();
            audioDestination =
              recordingAudioContext.createMediaStreamDestination();

            if (screenAudioTracks.length > 0) {
              const screenAudioSource =
                recordingAudioContext.createMediaStreamSource(
                  new MediaStream([screenAudioTracks[0]])
                );
              screenAudioSource.connect(audioDestination);
            }

            if (micAudioTracks.length > 0) {
              const micAudioSource =
                recordingAudioContext.createMediaStreamSource(
                  new MediaStream([micAudioTracks[0]])
                );
              micAudioSource.connect(audioDestination);
            }

            const mixedAudioTracks = audioDestination.stream.getAudioTracks();
            if (mixedAudioTracks.length > 0) {
              combinedStream.addTrack(mixedAudioTracks[0]);
            }
          } catch (mixError: any) {
            console.warn('音频混音失败，使用原始音频:', mixError.message);
            if (screenAudioTracks.length > 0) {
              combinedStream.addTrack(screenAudioTracks[0]);
            }
          }
        }

        // 4. 创建 MediaRecorder
        const options: MediaRecorderOptions = {
          mimeType: 'video/webm;codecs=vp9',
          videoBitsPerSecond: 2500000, // 2.5 Mbps
        };

        // 检查浏览器支持
        if (!MediaRecorder.isTypeSupported(options.mimeType!)) {
          options.mimeType = 'video/webm';
        }

        mediaRecorder = new MediaRecorder(combinedStream, options);

        // 监听数据可用事件
        mediaRecorder.ondataavailable = (event: BlobEvent) => {
          if (event.data && event.data.size > 0) {
            recordedChunks.push(event.data);
          }
        };

        // 监听录制停止事件
        mediaRecorder.onstop = () => {
          if (recordedChunks.length > 0) {
            const blob = new Blob(recordedChunks, { type: 'video/webm' });
            saveRecordedVideo(blob);
          }
          recordedChunks = [];
        };

        // 监听屏幕流停止事件（用户点击浏览器停止共享按钮）
        const videoTrack = combinedStream.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.onended = () => {
            if (mediaRecorder && mediaRecorder.state === 'recording') {
              mediaRecorder.stop();
              message.info('屏幕共享已停止，录制已保存');

              // 同步触发断开连接逻辑
              if (isConnected.value) {
                handleDisconnect();
              }
            }
          };
        }

        // 开始录制，每秒收集一次数据
        mediaRecorder.start(1000);

        // 提示音频源状态
        const hasSystemAudio = screenAudioTracks.length > 0;
        const hasMicAudio = micAudioTracks.length > 0;

        if (hasSystemAudio && hasMicAudio) {
          message.success('录制已启动：视频 + 系统音频 + 麦克风', 3);
          console.log('屏幕录制已启动，包含系统音频和麦克风');
        } else if (hasSystemAudio) {
          message.warning('录制已启动：视频 + 系统音频（未检测到麦克风）', 4);
          console.log('屏幕录制已启动，仅包含系统音频');
        } else if (hasMicAudio) {
          message.warning(
            '录制已启动：视频 + 麦克风（未检测到系统音频，请勾选"共享音频"）',
            4
          );
          console.log('屏幕录制已启动，仅包含麦克风音频');
        } else {
          message.warning('录制已启动：仅视频（未检测到任何音频源）', 5);
          console.log('屏幕录制已启动，无音频');
        }
      } catch (error: any) {
        // 用户取消选择屏幕
        if (error.name === 'NotAllowedError') {
          throw new Error('用户取消了屏幕选择');
        }
        throw error;
      }
    };

    /**
     * 处理连接点击
     */
    const handleConnectClick = (): void => {
      clearReconnectTimer();
      clearReconnectPrologueTimer();
      clearMuteKeepaliveTimer();
      suppressReconnectPrologue = false;
      isManualDisconnecting = false;
      isAutoReconnecting.value = false;
      reconnectAttempts.value = 0;
      isConnecting.value = true;
      initClient()
        .then(() => {
          return client.value?.connect();
        })
        .then(async () => {
          isConnected.value = true;
          connectStatus.value = 'connected';
          console.log('Coze realtime request payload:', {
            user_id: props.config.userId,
          });
          if (client.value) {
            applyRealtimeSessionSettings(client.value);
          }
          if (!isVolumeMuted.value) {
            setPlaybackVolume(volume.value);
          }

          try {
            await startScreenRecording();
          } catch (error: any) {
            console.warn('屏幕录制启动失败:', error.message || error);
            message.warning('连接成功，但屏幕录制未能启动');
          }
        })
        .catch((error: Error) => {
          if (process.env.NODE_ENV === 'development') {
            console.error(error);
          }
          const errorMessage = error.message || '连接失败，请检查网络或配置';
          message.error(errorMessage);
          connectStatus.value = 'disconnected';
        })
        .finally(() => {
          isConnecting.value = false;
        });
    };

    /**
     * 处理麦克风切换
     */
    const isServerNotConnectedError = (error: any): boolean =>
      /server not connected/i.test(error?.message || '');

    const wait = (ms: number): Promise<void> =>
      new Promise((resolve) => setTimeout(resolve, ms));

    const getReconnectDelay = (attempt: number): number => {
      const maxDelay = 32000;
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), maxDelay);
      const jitter = Math.random() * 500;
      return Math.round(delay + jitter);
    };

    const clearReconnectTimer = (): void => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const clearReconnectPrologueTimer = (): void => {
      if (reconnectPrologueTimer) {
        clearTimeout(reconnectPrologueTimer);
        reconnectPrologueTimer = null;
      }
    };

    const clearMuteKeepaliveTimer = (): void => {
      if (muteKeepaliveTimer) {
        clearInterval(muteKeepaliveTimer);
        muteKeepaliveTimer = null;
      }
    };

    const runMuteKeepalive = async (): Promise<void> => {
      if (
        !isMicrophoneMuted.value ||
        isManualDisconnecting ||
        isAutoReconnecting.value ||
        !client.value
      ) {
        return;
      }

      try {
        if (!client.value.isConnected) {
          await reconnectRealtimeClient();
          return;
        }

        if (!isMicrophoneMuted.value) {
          return;
        }

        await setMicrophoneMuted(true);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Mute keepalive failed:', error);
        }
      }
    };

    const startMuteKeepaliveTimer = (): void => {
      if (muteKeepaliveTimer) {
        return;
      }

      muteKeepaliveTimer = window.setInterval(() => {
        void runMuteKeepalive();
      }, muteKeepaliveIntervalMs);
      void runMuteKeepalive();
    };

    const attemptReconnect = async (): Promise<void> => {
      if (isManualDisconnecting) {
        return;
      }

      if (reconnectAttempts.value >= maxReconnectAttempts.value) {
        clearReconnectTimer();
        isAutoReconnecting.value = false;
        isConnected.value = false;
        connectStatus.value = 'disconnected';
        message.error(
          `自动重连失败次数达到上限 (${maxReconnectAttempts.value})，请手动点击重新连接`
        );
        return;
      }

      reconnectAttempts.value++;
      isConnected.value = false;
      connectStatus.value = 'reconnecting';
      isAutoReconnecting.value = true;

      const delay = getReconnectDelay(reconnectAttempts.value);
      message.warning(
        `连接断开，${Math.round(delay / 1000)}秒后尝试第 ${reconnectAttempts.value} 次重连`,
        3
      );

      clearReconnectTimer();
      reconnectTimer = window.setTimeout(async () => {
        try {
          const reconnectedClient = await reconnectRealtimeClient();
          if (!reconnectedClient) {
            throw new Error('重连失败');
          }

          reconnectAttempts.value = 0;
          isAutoReconnecting.value = false;
          isConnected.value = true;
          connectStatus.value = 'connected';
          await setMicrophoneMuted(isMicrophoneMuted.value);
          if (isMicrophoneMuted.value) {
            startMuteKeepaliveTimer();
          }
          setPlaybackVolume(isVolumeMuted.value ? 0 : volume.value);
          message.success('已重新连接');
        } catch (error) {
          console.error('重连失败:', error);
          isAutoReconnecting.value = false;
          await attemptReconnect();
        }
      }, delay);
    };

    const handleRealtimeDisconnect = (reason: string, event: any): void => {
      if (isManualDisconnecting || isAutoReconnecting.value) {
        return;
      }

      const errorCode =
        event?.data?.code ?? event?.code ?? event?.data?.error_code;
      if (errorCode === 4029 || errorCode === '4029') {
        console.warn('[Coze] 4029 房间静默超时断开', event);
        message.warning('连接因长时间静默超时，正在自动重连…', 4);
      }

      console.warn('Realtime disconnected:', reason, event);
      isConnected.value = false;
      connectStatus.value = 'disconnected';
      void attemptReconnect();
    };

    const reconnectRealtimeClient =
      async (): Promise<RealtimeClient | null> => {
        const oldClient = client.value as RealtimeClient | null;
        isConnecting.value = true;
        isConnected.value = false;
        connectStatus.value = 'reconnecting';
        isAutoReconnecting.value = true;
        suppressReconnectPrologue = true;
        clearReconnectPrologueTimer();
        setPlaybackVolume(0);
        try {
          await oldClient?.disconnect().catch(() => undefined);
          client.value = null;
          await initClient({ silentReconnect: true });
          const newClient = client.value as RealtimeClient | null;
          if (!newClient) {
            return null;
          }
          await newClient.connect();
          applyRealtimeSessionSettings(newClient);
          await newClient.interrupt().catch(() => undefined);
          reconnectPrologueTimer = window.setTimeout(() => {
            suppressReconnectPrologue = false;
            reconnectPrologueTimer = null;
            setPlaybackVolume(isVolumeMuted.value ? 0 : volume.value);
          }, 3000);
          isConnected.value = true;
          connectStatus.value = 'connected';
          return newClient;
        } finally {
          isConnecting.value = false;
          isAutoReconnecting.value = false;
        }
      };

    const ensureRealtimeConnected =
      async (): Promise<RealtimeClient | null> => {
        const currentClient = client.value as RealtimeClient | null;
        if (!currentClient) {
          return null;
        }
        if (currentClient.isConnected) {
          return currentClient;
        }

        return reconnectRealtimeClient();
      };

    const setAudioEnableWithRetry = async (
      targetClient: RealtimeClient,
      enabled: boolean
    ): Promise<void> => {
      let lastError: any;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await targetClient.setAudioEnable(enabled);
          return;
        } catch (error: any) {
          lastError = error;
          if (!isServerNotConnectedError(error) || attempt === 2) {
            throw error;
          }
          await wait(300 * (attempt + 1));
        }
      }
      throw lastError;
    };

    const enableAudioWithReconnect = async (
      currentClient: RealtimeClient,
      enabled: boolean
    ): Promise<RealtimeClient> => {
      try {
        await setAudioEnableWithRetry(currentClient, enabled);
        return currentClient;
      } catch (error: any) {
        if (!isServerNotConnectedError(error)) {
          throw error;
        }

        const reconnectedClient = await reconnectRealtimeClient();
        if (!reconnectedClient) {
          throw error;
        }
        await setAudioEnableWithRetry(reconnectedClient, enabled);
        return reconnectedClient;
      }
    };

    const setMicrophoneMuted = async (muted: boolean): Promise<void> => {
      const currentClient = await ensureRealtimeConnected();
      if (!currentClient) {
        return;
      }

      let activeClient = currentClient;
      let rtcEngine = activeClient.getRtcEngine();
      rtcEngine?.setCaptureVolume(
        StreamIndex.STREAM_INDEX_MAIN,
        muted ? 0 : 100
      );

      const localAudioTrack = rtcEngine?.getLocalStreamTrack(
        StreamIndex.STREAM_INDEX_MAIN,
        'audio'
      );
      if (localAudioTrack) {
        localAudioTrack.enabled = !muted;
      }

      activeClient = await enableAudioWithReconnect(currentClient, !muted);
      rtcEngine = activeClient.getRtcEngine();
      rtcEngine?.setCaptureVolume(
        StreamIndex.STREAM_INDEX_MAIN,
        muted ? 0 : 100
      );

      const activeAudioTrack = rtcEngine?.getLocalStreamTrack(
        StreamIndex.STREAM_INDEX_MAIN,
        'audio'
      );
      if (activeAudioTrack) {
        activeAudioTrack.enabled = !muted;
      }
    };

    const handleToggleMicrophone = async (): Promise<void> => {
      const previousMutedState = isMicrophoneMuted.value;
      const newMutedState = !previousMutedState;

      try {
        if (client.value) {
          if (!newMutedState) {
            clearMuteKeepaliveTimer();
          }
          isMicrophoneMuted.value = newMutedState;
          await setMicrophoneMuted(newMutedState);
          if (newMutedState) {
            startMuteKeepaliveTimer();
          } else {
            window.setTimeout(() => {
              if (!isMicrophoneMuted.value) {
                void setMicrophoneMuted(false);
              }
            }, 200);
            window.setTimeout(() => {
              if (!isMicrophoneMuted.value) {
                void setMicrophoneMuted(false);
              }
            }, 800);
          }
          message.success(
            isMicrophoneMuted.value ? '麦克风已静音' : '麦克风已开启'
          );
        }
      } catch (error: any) {
        message.error('切换麦克风状态失败: ' + error.message);
      }
    };

    /**
     * 设置播放音量
     * @param volumeLevel 音量级别 (0-100)
     */
    const setPlaybackVolume = (volumeLevel: number): void => {
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('Setting playback volume to:', volumeLevel);
        }

        const clampedVolume = Math.max(0, Math.min(100, volumeLevel)) / 100;

        // 1. 使用Web Audio API的GainNode控制全局音量（主要方式）
        if (!audioInitialized) {
          initWebAudioAPI();
        }

        scanAndConnectMediaElements();

        // 激活AudioContext
        if (audioContext && audioContext.state === 'suspended') {
          audioContext.resume().catch((error) => {
            if (process.env.NODE_ENV === 'development') {
              console.error('Failed to resume AudioContext:', error);
            }
          });
        }

        if (gainNode) {
          if (clampedVolume === 0) {
            gainNode.gain.setValueAtTime(0, audioContext!.currentTime);
          } else {
            // 使用线性插值实现平滑的音量变化
            gainNode.gain.setTargetAtTime(
              clampedVolume,
              audioContext!.currentTime,
              0.1
            );
          }
          if (process.env.NODE_ENV === 'development') {
            console.log(
              'Using Web Audio API GainNode to set volume:',
              clampedVolume
            );
          }
        }

        // 2. 尝试使用 RealtimeClient API 控制音量（如果可用）
        if (client.value && (client.value as any).setVolume) {
          try {
            (client.value as any).setVolume(clampedVolume);
            if (process.env.NODE_ENV === 'development') {
              console.log(
                'Using RealtimeClient API to set volume:',
                clampedVolume
              );
            }
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('RealtimeClient setVolume failed:', error);
            }
          }
        }

        if (client.value && (client.value as any).setAudioVolume) {
          try {
            (client.value as any).setAudioVolume(clampedVolume);
            if (process.env.NODE_ENV === 'development') {
              console.log(
                'Using RealtimeClient setAudioVolume to set volume:',
                clampedVolume
              );
            }
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('RealtimeClient setAudioVolume failed:', error);
            }
          }
        }

        // 3. 尝试使用 HTML5 Audio API 控制本地视频元素音量（备用方式1）
        const videoElement = document.querySelector(
          '#local-player video'
        ) as HTMLVideoElement;
        if (videoElement) {
          videoElement.volume = clampedVolume;
          if (process.env.NODE_ENV === 'development') {
            console.log('Set local video element volume:', clampedVolume);
          }
        }

        // 4. 尝试获取所有 audio 和 video 元素并设置音量（备用方式2）
        const allMediaElements = document.querySelectorAll('audio, video');

        allMediaElements.forEach((element) => {
          (element as HTMLMediaElement).volume = clampedVolume;
        });

        // 5. 尝试获取可能由 RealtimeClient 创建的音频元素（备用方式3）
        const cozeAudioElements = document.querySelectorAll(
          '[data-coze-audio], [class*="coze"][class*="audio"], [id*="audio"], [id*="player"]'
        );
        cozeAudioElements.forEach((element) => {
          if (element instanceof HTMLMediaElement) {
            element.volume = clampedVolume;
            if (process.env.NODE_ENV === 'development') {
              console.log(
                'Set Coze/Player audio element volume:',
                clampedVolume
              );
            }
          } else if (
            element instanceof HTMLDivElement ||
            element instanceof HTMLElement
          ) {
            // 如果是容器元素，尝试查找内部的音频元素
            const innerAudio = element.querySelector('audio');
            if (innerAudio instanceof HTMLMediaElement) {
              innerAudio.volume = clampedVolume;
            }
          }
        });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to set playback volume:', error);
        }
      }
    };

    /**
     * 处理音量静音切换
     */
    const handleToggleVolumeMute = (): void => {
      isVolumeMuted.value = !isVolumeMuted.value;
      if (isVolumeMuted.value) {
        setPlaybackVolume(0);
      } else {
        setPlaybackVolume(volume.value);
      }
      message.success(isVolumeMuted.value ? '音量已静音' : '音量已开启');
    };

    /**
     * 处理音量变化
     * @param value 音量值
     */
    const handleVolumeChange = (value: number): void => {
      volume.value = value;
      isVolumeMuted.value = value === 0;
      setPlaybackVolume(value);
    };

    /**
     * 处理打断对话
     */
    const handleInterrupt = (): void => {
      try {
        stopAgentThinking();
        if (client.value) {
          client.value.interrupt();
          message.success('已打断对话');
        }
      } catch (error: any) {
        message.error('打断对话失败: ' + error.message);
      }
    };

    /**
     * 处理断开连接
     */
    const handleDisconnect = (): void => {
      try {
        stopAgentThinking();
        isManualDisconnecting = true;
        clearReconnectTimer();
        clearReconnectPrologueTimer();
        clearMuteKeepaliveTimer();
        suppressReconnectPrologue = false;
        isAutoReconnecting.value = false;
        reconnectAttempts.value = 0;

        // 停止屏幕录制
        if (mediaRecorder && mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
        }

        // 停止所有媒体流
        if (screenStream) {
          screenStream.getTracks().forEach((track) => track.stop());
          screenStream = null;
        }
        if (microphoneStream) {
          microphoneStream.getTracks().forEach((track) => track.stop());
          microphoneStream = null;
        }
        if (combinedStream) {
          combinedStream.getTracks().forEach((track) => track.stop());
          combinedStream = null;
        }

        // 关闭录制用的 AudioContext
        if (recordingAudioContext) {
          recordingAudioContext.close();
          recordingAudioContext = null;
          audioDestination = null;
        }

        client.value?.disconnect();
        client.value = null;
        isConnected.value = false;
        connectStatus.value = 'disconnected';
        isSpeaking.value = false;
        forceStopAgentAvatar();
        isUserSpeaking.value = false;
        isVideoMinimized.value = false;
        restoreFromSplit();
        isMicrophoneMuted.value = false;
        isVolumeMuted.value = false;
        volume.value = 100;

        // 清理下载的图片
        downloadedImages.value.forEach((img) => {
          URL.revokeObjectURL(img.url);
        });
        downloadedImages.value = [];

        // 重置消息处理状态
        isSpeakingState = false;
        currentMessage = '';
        triggeredInCurrentMessage.clear();
        if (speakingTimeout) {
          clearTimeout(speakingTimeout);
          speakingTimeout = null;
        }
        hasLoggedConversationId = false;
        sessionStorage.removeItem('coze_conversation_id');
        void fetch('/api/coze/conversation/current', {
          method: 'DELETE',
          credentials: 'include',
        }).catch((error) => {
          console.error('Failed to clear Coze conversation ID:', error);
        });

        // 清理Web Audio API资源（音量控制）
        if (imageLinkSpeechRestoreTimer) {
          clearTimeout(imageLinkSpeechRestoreTimer);
          imageLinkSpeechRestoreTimer = null;
        }
        suppressingImageLinkSpeech = false;
        preMutedImageResponseSpeech = false;

        if (audioContext) {
          cleanupMediaObserver();
          audioContext.close();
          audioContext = null;
          gainNode = null;
          audioInitialized = false;
        }

        message.success('已断开连接');
      } catch (error: any) {
        const errorMessage = error.message || '断开失败';
        message.error(errorMessage);
      }
    };

    /**
     * 最小化视频
     */
    const minimizeVideo = (): void => {
      openSplitWindowsFromMain();
    };

    /**
     * 检测消息中的图片链接
     * @param content 消息内容
     * @returns 图片链接数组
     */
    const normalizeUrlToken = (url: string): string =>
      url
        .trim()
        .replace(/^["'`(<\[]+|[>"'`)\]]+$/g, '')
        .replace(/[.,;:，。；：]+$/g, '');

    const isImageLikeUrl = (url: string): boolean => {
      const normalizedUrl = normalizeUrlToken(url);
      return (
        /\.(jpg|jpeg|png|gif|webp|bmp|svg)(?:[?#][^\s]*)?$/i.test(
          normalizedUrl
        ) ||
        /(?:artifact|image(?:\/|%2F)|img(?:\/|%2F)|pic(?:\/|%2F)|obj(?:\/|%2F)|file_output|bot-studio-plugin|plugin-resource|tos-sign\.coze\.(?:cn|com))/i.test(
          normalizedUrl
        )
      );
    };

    const shouldSuppressImageLinkMessage = (
      content: string,
      imageLinks: string[],
      isCompleted: boolean
    ): boolean => {
      if (!isCompleted || imageLinks.length === 0) return false;
      return true;
    };

    const isLikelyImageGenerationReply = (
      content: string,
      imageLinks: string[]
    ): boolean => {
      if (imageLinks.length === 0) return false;

      const normalizedContent = content.replace(/\s+/g, '');
      return (
        /!\[[^\]]*\]\(https?:\/\//i.test(content) ||
        /(已为你生成|已为您生成|已经生成|已生成|生成好了|图片如下|链接如下|请查看|点击查看|示意图|解剖图|结构图|图片|图像|插图)/.test(
          normalizedContent
        )
      );
    };

    const suppressImageLinkSpeechPlayback = (): void => {
      if (suppressingImageLinkSpeech) return;

      suppressingImageLinkSpeech = true;

      if (imageLinkSpeechRestoreTimer) {
        clearTimeout(imageLinkSpeechRestoreTimer);
        imageLinkSpeechRestoreTimer = null;
      }

      if (client.value) {
        void client.value.interrupt().catch((error: unknown) => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to interrupt image-link speech:', error);
          }
        });
      }

      setPlaybackVolume(0);
      isSpeaking.value = false;
      forceStopAgentAvatar();
      isSpeakingState = false;
      stopLipSync();

      imageLinkSpeechRestoreTimer = window.setTimeout(() => {
        suppressingImageLinkSpeech = false;
        imageLinkSpeechRestoreTimer = null;

        if (!isVolumeMuted.value) {
          setPlaybackVolume(volume.value);
        }
      }, 1200);
    };

    const restorePlaybackAfterImageHandling = (): void => {
      if (imageLinkSpeechRestoreTimer) {
        clearTimeout(imageLinkSpeechRestoreTimer);
        imageLinkSpeechRestoreTimer = null;
      }

      suppressingImageLinkSpeech = false;
      preMutedImageResponseSpeech = false;

      if (!isVolumeMuted.value) {
        setPlaybackVolume(volume.value);
      }
    };

    const detectImageLinks = (content: string): string[] => {
      const links: string[] = [];
      const isVideoFrame = (url: string) => /video_frame_/i.test(url);
      const isPluginIcon = (url: string) => /plugin_icon/i.test(url);
      const pushLink = (url: string): void => {
        const normalizedUrl = normalizeUrlToken(url);
        if (
          normalizedUrl &&
          isImageLikeUrl(normalizedUrl) &&
          !isVideoFrame(normalizedUrl) &&
          !isPluginIcon(normalizedUrl) &&
          !links.includes(normalizedUrl)
        ) {
          links.push(normalizedUrl);
        }
      };

      // 1. 从 markdown 图片语法 ![alt](url) 中提取
      const plainUrlRegex = /https?:\/\/[^\s<>"'`)\]]+/gi;
      let plainMatch: RegExpExecArray | null;
      while ((plainMatch = plainUrlRegex.exec(content)) !== null) {
        pushLink(plainMatch[0]);
      }
      if (links.length > 0) return links;

      const mdImageRegex = /!\[[^\]]*\]\(([^)]+)\)/g;
      let match;
      while ((match = mdImageRegex.exec(content)) !== null) {
        pushLink(match[1]);
      }

      // 2. 从反引号包裹的链接中提取（例如 `https://example.com/image.png`）
      const backtickLinkRegex = /`(https?:\/\/[^`]+)`/gi;
      while ((match = backtickLinkRegex.exec(content)) !== null) {
        pushLink(match[1]);
      }

      // 3. 从普通文本中的链接提取（例如 https://example.com/image.png）
      const plainLinkRegex =
        /https?:\/\/[^\s"'\),，。]+\.(jpg|jpeg|png|gif|webp|bmp|svg)\b/g;
      while ((match = plainLinkRegex.exec(content)) !== null) {
        const url = match[0];
        if (
          url &&
          !isVideoFrame(url) &&
          !isPluginIcon(url) &&
          !links.includes(url)
        ) {
          links.push(url);
        }
      }

      return links;
    };

    /**
     * 下载图片
     * @param url 图片URL
     */
    const downloadImage = async (
      url: string,
      showToast = true
    ): Promise<void> => {
      try {
        if (!url || typeof url !== 'string') {
          throw new Error('Invalid image URL');
        }

        const alreadyDownloaded = downloadedImages.value.some(
          (img) => img.originalUrl === url || img.url === url
        );
        if (alreadyDownloaded) {
          return;
        }

        let response: Response;
        try {
          response = await fetch(url, {
            mode: 'cors',
            redirect: 'follow',
            headers: { Accept: 'image/*,*/*;q=0.8' },
            credentials: 'omit',
          });
        } catch (fetchError) {
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              'Fetch failed for image, using direct URL:',
              fetchError
            );
          }
          downloadedImages.value.push({
            url: url,
            originalUrl: url,
            timestamp: Date.now(),
          });
          if (showToast) message.info('图片已生成，点击可查看');
          return;
        }

        if (!response.ok) {
          if (response.status === 403) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('Image URL returned 403, using direct URL:', url);
            }
            downloadedImages.value.push({
              url: url,
              originalUrl: url,
              timestamp: Date.now(),
            });
            if (showToast) message.info('图片已生成，点击可查看');
            return;
          }
          throw new Error(
            `Failed to fetch image: ${response.status} ${response.statusText}`
          );
        }

        const contentType = response.headers.get('content-type') || '';
        const blob = await response.blob();

        const isImageBlob =
          blob.type.startsWith('image/') ||
          contentType.toLowerCase().includes('image') ||
          (blob.type === '' &&
            /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url)) ||
          (blob.type === 'application/octet-stream' &&
            blob.size > 1024 &&
            /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url));

        if (!isImageBlob) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Non-image blob type, checking by extension:', {
              blobType: blob.type,
              contentType,
              size: blob.size,
              url,
            });
          }
          if (
            blob.size > 1024 &&
            /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url)
          ) {
            if (process.env.NODE_ENV === 'development') {
              console.log('Accepting blob based on URL extension match');
            }
          } else if (blob.size > 0) {
            if (process.env.NODE_ENV === 'development') {
              console.log('Accepting non-empty blob with content');
            }
          } else {
            throw new Error('Response is empty or not an image');
          }
        }

        const imageUrl = URL.createObjectURL(blob);

        downloadedImages.value.push({
          url: imageUrl,
          originalUrl: url,
          timestamp: Date.now(),
        });

        if (showToast) message.success('图片已生成，点击可查看');

        setTimeout(() => {
          const index = downloadedImages.value.findIndex(
            (img) => img.url === imageUrl
          );
          if (index !== -1) {
            downloadedImages.value.splice(index, 1);
            URL.revokeObjectURL(imageUrl);
          }
        }, 60000);
      } catch (error: any) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error downloading image:', error.message || error);
          console.error('Image URL:', url);
        }
        downloadedImages.value.push({
          url: url,
          originalUrl: url,
          timestamp: Date.now(),
        });
        if (showToast) message.warning('图片已生成，点击可查看');

        setTimeout(() => {
          const index = downloadedImages.value.findIndex(
            (img) => img.originalUrl === url
          );
          if (index !== -1) {
            downloadedImages.value.splice(index, 1);
          }
        }, 60000);
      }
    };

    /**
     * 打开图片预览弹窗
     * @param url 图片URL
     */
    const openImagePreview = (url: string): void => {
      previewImageUrl.value = url;
      imagePreviewVisible.value = true;
    };

    /**
     * 关闭图片预览弹窗
     */
    const closeImagePreview = (): void => {
      imagePreviewVisible.value = false;
      previewImageUrl.value = '';
    };

    /**
     * 移除图片提示
     * @param index 图片索引
     */
    const removeImageToast = (index: number): void => {
      downloadedImages.value.splice(index, 1);
    };

    /**
     * 保存图片到本地
     * @param url 图片URL（可能是 blob URL 或远程 URL）
     */
    const saveImageToFile = async (url: string): Promise<void> => {
      try {
        let blobUrl = url;
        // 如果是远程 URL，先 fetch 转为 blob
        if (!url.startsWith('blob:')) {
          const response = await fetch(url, { mode: 'cors' });
          if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
          const blob = await response.blob();
          blobUrl = URL.createObjectURL(blob);
        }

        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `image_${Date.now()}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // 如果是新创建的 blob URL，延迟释放
        if (blobUrl !== url) {
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        }
        message.success('图片保存成功');
      } catch (error: any) {
        console.error('保存图片失败:', error);
        message.error('保存图片失败');
      }
    };

    /**
     * 更新剩余对话时间
     * @param duration 消耗的时间（秒）
     */
    const updateRemainingTime = async (duration: number): Promise<void> => {
      try {
        const response = await fetch('/api/time/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ duration }),
          credentials: 'include',
        });
        if (!response.ok) {
          const data = await response.json();
          if (data.detail === '对话时间已用完') {
            message.error('对话时间已用完，请联系管理员');
            // 可以在这里添加断开连接的逻辑
          }
        }
      } catch (error) {
        console.error('更新剩余时间失败:', error);
      }
    };

    /**
     * 过滤语音合成中的图片链接
     * @param content 消息内容
     * @returns 过滤后的内容
     */
    const filterImageLinksForSpeech = (content: string): string => {
      if (!content) return content;

      const isImageLikeUrl = (url: string): boolean => {
        const normalizedUrl = url
          .trim()
          .replace(/^["'`(<\[]+|[>"'`)\]]+$/g, '');
        return (
          /\.(jpg|jpeg|png|gif|webp|bmp|svg)(?:[?#][^\s]*)?$/i.test(
            normalizedUrl
          ) ||
          /(?:artifact|image(?:\/|%2F)|img(?:\/|%2F)|pic(?:\/|%2F)|obj(?:\/|%2F)|file_output|bot-studio-plugin|plugin-resource|tos-sign\.coze\.(?:cn|com))/i.test(
            normalizedUrl
          )
        );
      };

      let filtered = content;

      // 过滤 markdown 图片语法 ![alt](url)
      filtered = filtered.replace(/!\[[^\]]*\]\(([^)]+)\)/g, (match, url) => {
        if (isImageLikeUrl(url)) {
          return '';
        }
        return match;
      });

      // 过滤 markdown 链接语法 [text](url)（保留文字部分）
      filtered = filtered.replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        (match, text, url) => {
          if (isImageLikeUrl(url)) {
            return '';
          }
          return text;
        }
      );

      // 过滤反引号包裹的链接 `url`
      filtered = filtered.replace(/`([^`]+)`/g, (match, url) => {
        if (/^https?:\/\//.test(url) || isImageLikeUrl(url)) {
          return '';
        }
        return match;
      });

      // 过滤 video_frame URL
      filtered = filtered.replace(/https?:\/\/[^\s]*video_frame_[^\s]*/gi, '');

      // 过滤裸图片链接和图片 CDN 链接
      filtered = filtered.replace(/https?:\/\/[^\s<>"'`)\]]+/gi, (match) =>
        isImageLikeUrl(match) ? '' : match
      );

      // 过滤流式返回里常见的图片链接碎片，避免 delta 分片时被念出来
      filtered = filtered.replace(/!\[[^\]]*\]\(?/g, '');
      filtered = filtered.replace(/\bhttps?:?(?:\/\/?)?\b/gi, '');
      filtered = filtered.replace(
        /\b(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+){1,}(?:\/[^\s]*)?/gi,
        (match) => {
          if (isImageLikeUrl(match)) {
            return '';
          }
          return match;
        }
      );

      // 过滤描述图片链接的文字
      filtered = filtered.replace(
        /(图片链接|照片链接|图片地址|照片地址|生成图链接|链接如下|链接地址|链接为|链接是|链接|链接：|链接:|点击链接查看|点击查看|点击链接|请查看|查看详情)/g,
        ''
      );
      // 过滤多余的标点符号
      filtered = filtered.replace(/[，。：:,]/g, ' ');
      // 移除多余的空格
      filtered = filtered.replace(/\s+/g, ' ');
      return filtered.trim();
    };

    /**
     * ★ 新增：清理语音转录中的重复/卡壳文本
     * 例如: "生成生成一张生成一张手鼓图" -> "生成一张手鼓图"
     * @param text 原始转录文本
     * @returns 清理后的文本
     */
    const cleanStuttering = (text: string): string => {
      if (!text || text.length < 4) return text;

      // 移除连续的重复字符（3次及以上）
      let cleaned = text.replace(/(.)\1{2,}/g, '$1');

      // 移除重复的词语模式，如 "生成生成" -> "生成"
      // 匹配连续两个相同词语
      cleaned = cleaned.replace(/(\S+)(\s*)(\1)+/g, '$1$2');

      // 移除句子级别的重复，如 "生成一张手鼓图生成一张手鼓图"
      // 只保留最后一个完整片段
      const sentenceRepeat = /(\S.{10,})(\s*)(\1)+/g;
      cleaned = cleaned.replace(sentenceRepeat, '$1$2');

      return cleaned.trim();
    };

    /**
     * ★ 新增：检测用户语音转录文本中是否包含图片生成意图
     * 只有明确说出画图相关关键词才返回 true
     * @param text 用户语音转录文本
     * @returns 是否包含图片生成意图
     */
    const detectImageIntent = (text: string): boolean => {
      if (!text || typeof text !== 'string') return false;
      const cleanedText = cleanStuttering(text);
      const lowerText = cleanedText.toLowerCase();

      // 强关键词：自身就包含明确的图片/图像含义，无需额外上下文
      const strongKeywords = [
        '画图',
        '生成图片',
        '生成图像',
        '画画',
        '作图',
        '画一张图',
        '画一张',
        '画一幅',
        '生成一张',
        '生成一幅',
        '绘制',
        '图片生成',
        '图像生成',
        '文生图',
        '图生图',
        'draw',
        'paint',
        'generate image',
        'create image',
        'make a picture',
        '画个图',
        '做个图',
        '做个图片',
        '来张图',
        '发张图',
        '来一张图',
        '发一张图',
        '来幅画',
        '画幅画',
        '生成一张图',
      ];
      if (strongKeywords.some((k) => lowerText.includes(k))) return true;

      // 弱关键词：需要后面跟着图片相关的对象词才触发
      const weakPatterns = [
        /(?:帮我画|给我画|画个|画一个|帮我生成|给我生成|生成|画出|创作一幅|帮我做个图?)\s*(?:一[张幅个])?\s*(?:图|画|像|照片|插图|手绘图|素描|海报|头像|壁纸|漫画|插画|Logo|logo|LOGO|水彩|油画|风景|人物|动物|建筑|食物|花草|植物|星空|太阳|月亮|山水|海洋|森林|城市|乡村|卡通|动漫|示意图|流程图|结构图|思维导图|架构图|思维图)/,
        /(?:帮我画|给我画|画个|画一个|帮我生成|给我生成|生成|画出|创作一幅|帮我做个图?)\s*(?:一[张幅个])?\s*(?:手骨|手鼓|骨骼|解剖|器官|人体|脸|猫|狗|花|树|房子|车|飞机|机器人|外星人|恐龙|公主|城堡|蛋糕|冰淇淋|太阳|月亮|星星|彩虹)/,
      ];
      if (weakPatterns.some((p) => p.test(lowerText))) return true;

      return false;
    };

    /**
     * 总结对话
     */
    const handleSummarize = async (): Promise<void> => {
      if (client.value && isConnected.value) {
        try {
          // 使用 Coze 协议格式发送文本消息给智能体
          // 措辞与语音指令一致，确保智能体在相同的对话上下文中处理
          await client.value.sendMessage({
            id: `event_${Date.now()}`,
            event_type: 'conversation.message.create',
            data: {
              role: 'user',
              content_type: 'text',
              content: '总结本次对话',
            },
          });
          startAgentThinking();
          message.info('已请求智能体总结对话');
        } catch (error) {
          console.error('发送总结请求失败:', error);
          message.error('发送总结请求失败');
        }
      }
    };

    /**
     * 退出登录
     */
    /**
     * 返回模块选择
     */
    const handleLogout = (): void => {
      emit('goBack');
    };

    // 添加窗口大小变化监听
    onMounted(() => {
      syncLayoutMode();
      syncAvatarStateToWindow();
      publishConnectionBridge();

      Object.defineProperty(window, '显示对话', {
        configurable: true,
        get() {
          showTextChat();
          return '文字聊天已显示';
        },
      });
      Object.defineProperty(window, 'showTextChat', {
        configurable: true,
        value: showTextChat,
      });

      nextTick(() => {
        syncLayoutMode();
        requestAnimationFrame(() => {
          syncLayoutMode();
        });
      });

      window.addEventListener('resize', syncLayoutMode);
      window.addEventListener('orientationchange', syncLayoutMode);
      window.addEventListener('pageshow', syncLayoutMode);
      window.addEventListener('keydown', handleSplitWindowsShortcut);
    });

    // 组件卸载时移除监听
    const cleanup = () => {
      isManualDisconnecting = true;
      clearReconnectTimer();
      clearReconnectPrologueTimer();
      clearMuteKeepaliveTimer();
      suppressReconnectPrologue = false;
      isAutoReconnecting.value = false;
      reconnectAttempts.value = 0;

      delete (window as unknown as Record<string, unknown>)['显示对话'];
      delete (window as unknown as Record<string, unknown>)['showTextChat'];
      window.removeEventListener('resize', syncLayoutMode);
      window.removeEventListener('orientationchange', syncLayoutMode);
      window.removeEventListener('pageshow', syncLayoutMode);
      window.removeEventListener('keydown', handleSplitWindowsShortcut);
      clearSplitWindowMonitor();
      closeSplitWindows();
      clearConnectionBridge();

      // 清理屏幕录制资源
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
      if (screenStream) {
        screenStream.getTracks().forEach((track) => track.stop());
        screenStream = null;
      }
      if (microphoneStream) {
        microphoneStream.getTracks().forEach((track) => track.stop());
        microphoneStream = null;
      }
      if (combinedStream) {
        combinedStream.getTracks().forEach((track) => track.stop());
        combinedStream = null;
      }
      if (recordingAudioContext) {
        recordingAudioContext.close();
        recordingAudioContext = null;
        audioDestination = null;
      }
      cleanupMediaObserver();
      if (audioContext) {
        audioContext.close();
        audioContext = null;
        gainNode = null;
        audioInitialized = false;
      }
      client.value?.disconnect();
      client.value = null;
      recordedChunks = [];
      stopAgentThinking();
    };

    onUnmounted(() => {
      cleanup();
    });

    // 获取头像图片
    const fetchAvatarImage = async () => {
      try {
        console.log('开始获取头像图片');
        // 添加时间戳参数，避免浏览器缓存
        const url = `/api/avatar/current?timestamp=${Date.now()}`;
        const response = await fetch(url, {
          credentials: 'include',
        });
        console.log('获取头像图片响应:', response);
        if (response.ok) {
          const data = await response.json();
          console.log('获取头像图片数据:', data);
          avatarImage.value = data.avatar_image || '/xingxiaonge6c.gif';
          console.log('更新头像图片:', avatarImage.value);
        }
      } catch (error) {
        console.error('获取头像图片失败:', error);
      }
    };

    // 初始化时获取头像图片
    fetchAvatarImage();

    // 监听连接状态变化，刷新头像图片
    watch(
      () => isConnected.value,
      (newValue) => {
        if (newValue) {
          // 连接成功后，刷新头像图片
          fetchAvatarImage();
        }
      }
    );

    // 监听配置变化
    watch(
      () => props.config,
      (newConfig) => {
        // 当配置变化时，更新头像图片
        if (newConfig.avatarImage) {
          avatarImage.value = newConfig.avatarImage;
        }
      },
      { deep: true }
    );

    watch(
      [avatarImage, isAgentSpeaking, isAgentThinking],
      () => {
        syncAvatarStateToWindow();
      },
      { immediate: true }
    );

    watch(
      [
        isConnected,
        isConnecting,
        isMicrophoneMuted,
        isVolumeMuted,
        volume,
        isPortraitMode,
        isWindowsSplit,
        isAgentSpeaking,
        isAgentThinking,
        avatarImage,
        connectStatus,
      ],
      () => {
        publishConnectionBridge();
      }
    );

    return {
      isConnected,
      isConnecting,
      connectStatus,
      reconnectAttempts,
      maxReconnectAttempts,
      isVideoMinimized,
      isMicrophoneMuted,
      isVolumeMuted,
      volume,
      isAgentSpeaking,
      isAgentThinking,
      isUserSpeaking,
      isPortraitMode,
      isWindowsSplit,
      downloadedImages,
      imagePreviewVisible,
      previewImageUrl,
      avatarImage,
      openImagePreview,
      closeImagePreview,
      removeImageToast,
      saveImageToFile,
      handleConnectClick,
      handleDisconnect,
      minimizeVideo,
      handleToggleMicrophone,
      handleToggleVolumeMute,
      handleVolumeChange,
      handleInterrupt,
      handleSummarize,
      handleToggleSplitWindows,
      restoreFromSplit,
      handleLogout,
      showTextChatPanel,
      inputMessage,
      chatMessages,
      textChatScrollContainer,
      textChatInputRef,
      hideTextChatPanel,
      sendTextMessage,
      goBack: () => emit('goBack'),
      cleanup,
    };
  },
});
</script>

<style scoped>
.connection-manager {
  width: 100%;
  height: 100%;
}

.main-interface {
  width: calc(100% - 10px);
  height: calc(100% - 10px);
  min-height: 600px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  background: var(--bg-card);
  backdrop-filter: var(--blur-light);
  -webkit-backdrop-filter: var(--blur-light);
  border-radius: var(--border-radius);
  padding: 10px 20px;
  box-shadow: var(--shadow-lg);
  border: var(--border-primary);
  margin: 5px;
}

.card-container {
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 20px;
  flex: 1;
  min-height: 460px;
}

.text-chat-panel {
  position: absolute;
  left: 50%;
  bottom: 118px;
  z-index: 30;
  width: min(560px, calc(100% - 32px));
  transform: translateX(-50%);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.94);
  border: var(--border-primary);
  box-shadow: var(--shadow-lg);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  overflow: hidden;
}

.text-chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(0, 184, 217, 0.18);
}

.text-chat-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.text-chat-hint {
  font-size: 12px;
  font-weight: 400;
  color: var(--text-secondary);
}

.text-chat-close {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--text-secondary);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}

.text-chat-close:hover {
  background: rgba(0, 184, 217, 0.1);
  color: var(--text-primary);
}

.text-chat-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px 14px;
}

.text-chat-messages {
  max-height: 180px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.text-chat-empty {
  padding: 18px 0;
  text-align: center;
  color: var(--text-secondary);
  font-size: 13px;
}

.text-chat-message {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.text-chat-message.user {
  align-items: flex-end;
}

.text-chat-message.agent {
  align-items: flex-start;
}

.text-chat-role {
  font-size: 12px;
  color: var(--text-secondary);
}

.text-chat-content {
  max-width: min(86%, 420px);
  padding: 8px 12px;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.05);
  color: var(--text-primary);
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
}

.text-chat-message.user .text-chat-content {
  background: rgba(0, 180, 255, 0.14);
}

.text-chat-input-area {
  display: flex;
  gap: 10px;
}

.text-chat-input-area textarea {
  flex: 1;
  min-height: 52px;
  resize: none;
  border-radius: 12px;
  border: var(--border-primary);
  background: rgba(255, 255, 255, 0.9);
  padding: 10px 12px;
  color: var(--text-primary);
  font-size: 14px;
  font-family: inherit;
}

.text-chat-send {
  min-width: 72px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #0ea5e9, #2563eb);
  color: #fff;
  cursor: pointer;
  font-weight: 600;
}

.text-chat-send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.main-interface.windows-split {
  justify-content: flex-end;
  gap: 12px;
  min-height: unset;
}

.split-mode-banner {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 160px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px dashed rgba(0, 184, 217, 0.28);
  color: var(--text-secondary);
  text-align: center;
  padding: 20px 24px;
}

.split-mode-banner p {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.split-mode-banner span {
  font-size: 13px;
}

.rtc-hidden-mount {
  position: fixed;
  width: 1px;
  height: 1px;
  left: -9999px;
  top: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  z-index: -1;
}

.split-restore-btn {
  margin-top: 8px;
  border: none;
  border-radius: 999px;
  padding: 10px 20px;
  cursor: pointer;
  color: #fff;
  background: linear-gradient(135deg, #0ea5e9, #2563eb);
  box-shadow: 0 8px 18px rgba(14, 165, 233, 0.28);
}

.split-restore-btn:hover {
  filter: brightness(1.05);
}

/* 返回按钮 */
.back-btn {
  position: absolute;
  top: 20px;
  left: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 20px;
  background: var(--bg-card);
  border: var(--border-primary);
  box-shadow: var(--shadow-sm);
  color: var(--text-secondary);
  font-size: 14px;
  cursor: pointer;
  transition: all var(--transition-normal);
  z-index: 10;
  backdrop-filter: var(--blur-light);
  -webkit-backdrop-filter: var(--blur-light);
}

.back-btn:hover {
  color: var(--accent-cyan);
  background: var(--bg-card-hover);
  box-shadow: var(--shadow-glow-cyan);
  transform: translateY(-2px);
}

/* 移动端媒体查询 */
@media (max-width: 768px) {
  .back-btn {
    top: 10px;
    left: 10px;
    padding: 6px 12px;
    font-size: 12px;
  }

  .main-interface {
    width: calc(100% - 20px);
    height: calc(100% - 20px);
    min-height: unset;
    padding: 10px;
    gap: 10px;
  }

  .card-container {
    min-height: unset;
    gap: 10px;
  }

  .status-indicator {
    top: 10px;
    right: 10px;
    padding: 3px 10px;
    font-size: 12px;
  }

  .image-download-toast {
    bottom: 10px;
    right: 10px;
    padding: 8px;
    gap: 8px;
  }

  .image-thumbnail {
    width: 50px;
    height: 50px;
  }

  .image-toast-content p {
    font-size: 12px;
  }

  .image-toast-hint {
    font-size: 10px !important;
  }
}

.status-indicator {
  position: absolute;
  top: 20px;
  right: 20px;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: var(--blur-light);
  -webkit-backdrop-filter: var(--blur-light);
  color: var(--text-secondary);
  z-index: 10;
  border: 1px solid rgba(0, 184, 217, 0.2);
  box-shadow: 0 4px 12px rgba(0, 80, 180, 0.08);
}

.status-indicator.connected {
  background: rgba(230, 255, 250, 0.85);
  backdrop-filter: var(--blur-light);
  -webkit-backdrop-filter: var(--blur-light);
  color: var(--success-color);
  box-shadow: 0 4px 12px rgba(0, 185, 107, 0.15);
  border: 1px solid rgba(0, 185, 107, 0.25);
}

.status-indicator.reconnecting {
  background: rgba(255, 247, 230, 0.9);
  color: #d97706;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.16);
  border: 1px solid rgba(245, 158, 11, 0.28);
}

.video-minimized .card-container {
  flex-direction: column;
}

/* 竖屏模式样式 */
.main-interface.portrait {
  flex-direction: row;
  align-items: stretch;
  gap: 0;
  padding: 10px;
}

.card-container.portrait {
  flex: 1;
  flex-direction: column;
  gap: 0;
  min-height: unset;
}

.connection-manager.portrait-mode .main-interface {
  flex-direction: row;
  align-items: stretch;
}

.connection-manager.portrait-mode .card-container {
  flex: 1;
  flex-direction: column;
  gap: 0;
}

/* 图片下载提示样式 */
.image-download-toast {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: var(--bg-card);
  backdrop-filter: var(--blur-light);
  -webkit-backdrop-filter: var(--blur-light);
  border-radius: 8px;
  box-shadow: var(--shadow-lg);
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 1000;
  animation: slideIn 0.3s ease-out;
  border: var(--border-primary);
}

.image-thumbnail {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.image-thumbnail:hover {
  transform: scale(1.05);
}

.image-toast-content {
  flex: 1;
  min-width: 0;
}

.image-toast-content p {
  margin: 0;
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
}

.image-toast-hint {
  font-size: 12px !important;
  color: var(--text-secondary) !important;
  margin-top: 2px !important;
}

.image-toast-close {
  background: transparent !important;
  border: none !important;
  color: var(--text-tertiary) !important;
  padding: 4px !important;
}

.image-toast-close:hover {
  color: var(--text-primary) !important;
}

.image-toast-actions {
  display: flex;
  gap: 4px;
  align-items: center;
}

.image-toast-download {
  background: transparent !important;
  border: none !important;
  color: var(--accent-cyan) !important;
  padding: 4px !important;
}

.image-toast-download:hover {
  color: var(--accent-blue) !important;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* 图片预览弹窗样式 */
.image-preview-modal :deep(.ant-modal-content) {
  background: rgba(0, 0, 0, 0.9) !important;
  border-radius: 12px !important;
  padding: 10px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5) !important;
}

.image-preview-modal :deep(.ant-modal-close) {
  color: #fff !important;
}

.image-preview-modal :deep(.ant-modal-close:hover) {
  background: rgba(255, 255, 255, 0.2) !important;
}

.preview-image {
  max-width: 80vw;
  max-height: 75vh;
  border-radius: 8px;
  object-fit: contain;
}

.preview-container {
  padding: 10px;
}

.preview-download-btn {
  margin-top: 12px;
}
</style>
